import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Quote, Heart, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function AcceptComplimentsGuide() {
  const navigate = useNavigate();

  const wrongResponses = [
    { response: "Oh, this old thing?", why: "Dismisses the giver's perception" },
    { response: "I was just lucky", why: "Undermines your actual effort" },
    { response: "It wasn't that good", why: "Contradicts and insults the giver" },
    { response: "Yeah, but...", why: "Immediately deflects to a negative" },
    { response: "*Ignores or changes subject*", why: "Creates awkwardness" }
  ];

  const rightResponses = [
    "Thank you!",
    "Thank you, that means a lot.",
    "Thank you, I appreciate you noticing.",
    "Thank you, I worked hard on that.",
    "Thank you, you made my day!",
    "Thank you, that's very kind of you."
  ];

  const practice = [
    { scenario: "Someone compliments your outfit", say: "Thank you! I feel great in this." },
    { scenario: "Boss praises your work", say: "Thank you, I really put effort into it." },
    { scenario: "Friend says you look good", say: "Thank you! That's so nice of you." },
    { scenario: "Stranger compliments something", say: "Thank you so much!" },
    { scenario: "Partner gives you a compliment", say: "Thank you, that means everything to me." }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-4">
            Week 7 • Day 5 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Accept Compliments Gracefully
          </h1>
          <p className="text-xl text-secondary-600">Say Thank You and Mean It</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Heart className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Mission</h3>
              <p className="text-pink-100 text-lg">
                When you receive any compliment today, stop your automatic deflection. 
                Say "Thank you" and nothing more. Smile. Let it land. Practice receiving.
              </p>
            </div>
          </div>
        </section>

        {/* What Not To Do */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Common Mistakes</h2>
          <div className="space-y-3">
            {wrongResponses.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-3 bg-red-50 rounded-lg">
                <span className="text-red-500 font-bold">✗</span>
                <div>
                  <span className="font-medium text-red-700">"{item.response}"</span>
                  <p className="text-sm text-secondary-600">{item.why}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What To Say */}
        <section className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border-2 border-pink-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-pink-500" />
            What to Say Instead
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {rightResponses.map((response, i) => (
              <div key={i} className="bg-white rounded-lg p-4 text-center">
                <Quote className="w-5 h-5 text-pink-400 mx-auto mb-2" />
                <p className="text-pink-700 font-medium">"{response}"</p>
              </div>
            ))}
          </div>
        </section>

        {/* Practice Scenarios */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Practice Scenarios</h2>
          <div className="space-y-4">
            {practice.map((item, index) => (
              <div key={index} className="p-4 bg-pink-50 rounded-xl">
                <p className="text-secondary-700 mb-2"><strong>Scenario:</strong> {item.scenario}</p>
                <p className="text-pink-700"><strong>Say:</strong> "{item.say}"</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default AcceptComplimentsGuide;
